import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { authApi } from '@/api/auth'
import { useToast } from '@/composables/useToast'
import { getResponseStatus, HTTP_STATUS_UNAUTHORIZED } from '@/lib/redirect'
import { useProjectsStore } from '@/stores/projects'
import { useVoicePlanUploadStore } from '@/stores/voicePlanUpload'
import type { LoginPayload, User } from '@/types/models'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  // Tracks whether the initial GET /auth/user probe on app load has resolved.
  const initialized = ref(false)
  const loading = ref(false)

  const isAuthenticated = computed(() => user.value !== null)

  async function login(payload: LoginPayload): Promise<void> {
    loading.value = true
    try {
      user.value = await authApi.login(payload)
      initialized.value = true
    } finally {
      loading.value = false
    }
  }

  // App-load probe. Swallows a 401 (not logged in) but rethrows real errors.
  async function fetchUser(): Promise<void> {
    loading.value = true
    try {
      user.value = await authApi.user()
    } catch (e: unknown) {
      const status = getResponseStatus(e)
      if (status === HTTP_STATUS_UNAUTHORIZED) {
        // Also clears the projects store (see reset() below) — without this,
        // a session that expired silently (no explicit logout click) would
        // leave the previous user's project list and active-project
        // selection sitting in memory for whoever logs in next on this tab.
        reset()
      } else {
        throw e
      }
    } finally {
      initialized.value = true
      loading.value = false
    }
  }

  // Clears the session locally even if the API call fails — the top bar's
  // logout action navigates to /login right after this either way, and a
  // stale client-side session left behind by a failed request would be
  // confusing to leave the user in.
  async function logout(): Promise<void> {
    try {
      await authApi.logout()
    } finally {
      reset()
    }
  }

  // Also clears the projects store (project list/selection), the
  // voicePlanUpload store (any pending failed-upload blob and error), and
  // any in-flight toasts — none of that state should leak into the next
  // session on this tab (logout doesn't reload the page, so module-level
  // state otherwise survives it). Without clearing the upload store, a
  // failed upload left pending when the session ends would surface as a
  // retryable "Upload failed" indicator for whoever logs in next, and
  // tapping retry would send the previous user's recorded audio under the
  // new session.
  function reset(): void {
    user.value = null
    useProjectsStore().reset()
    useVoicePlanUploadStore().dismiss()
    useToast().reset()
  }

  return { user, initialized, loading, isAuthenticated, login, fetchUser, logout }
})
